<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ContractManagementExtractionInDb } from '@/api';
import { isValueOverwritten } from '@/utils/overwrite';
import { formatDisplayValue } from '@/utils/displayAiResponse';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaOverwriteBadge from '@/components/design/domain/extraction/GaiaOverwriteBadge.vue';
import { taskStatusMap } from '@/components/design/statusMaps';
import { gaiaAccent, gaiaFonts, gaiaNeutral, gaiaStatus } from '@/components/design/themes/tokens';

// ==================== Props & Emits ====================
interface Props {
  contractFileUid: string;
  isSplitView?: boolean;
  // Data passed from parent to avoid refetching
  extractions: ContractManagementExtractionInDb[];
  totalExtractions: number;
  originalTotalExtractions: number;
  loading: boolean;
  extractionsLoaded: boolean;
}

const skeletonCells = ['80%', { width: '55%', soft: true }, { shape: 'pill' }] as const;

const props = defineProps<Props>();

const emit = defineEmits<{
  editExtraction: [extraction: ContractManagementExtractionInDb];
  pageClick: [page: number, extraction: ContractManagementExtractionInDb];
  openDocument: [];
  fetchExtractions: [
    params?: {
      text?: string;
      page?: number;
      pageSize?: number;
      sortKey?: string;
      sortDirection?: 'asc' | 'desc';
    },
  ];
}>();

// ==================== State ====================
// Search & Pagination (local state for UI)
const searchQuery = ref<string>('');
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);

/**
 * The mock's three tracks. The last one holds the page chip and the hover Edit,
 * so it sits hard right and the table needs no separate action track.
 *
 * Beside the document the tracks drop to their tightest floors: the page chip
 * is what the split view is *for* — jumping the PDF to the source — and at the
 * full-width floors it lands past the pane's edge, behind a sideways scroll.
 */
const columns = computed<GaiaDataColumn[]>(() =>
  props.isSplitView
    ? [
        { key: 'prompt', label: 'Field', width: 'minmax(160px,1.5fr)' },
        { key: 'value', label: 'Value', width: 'minmax(110px,1fr)' },
        { key: 'page', label: 'Page · status', width: '140px', align: 'right' },
      ]
    : [
        { key: 'prompt', label: 'Field', width: 'minmax(280px,1.6fr)' },
        { key: 'value', label: 'Value', width: 'minmax(180px,1fr)' },
        { key: 'page', label: 'Page · status', width: '168px', align: 'right' },
      ]
);

// ==================== Functions ====================
/**
 * Request parent to fetch extractions with current search/pagination parameters
 */
function fetchExtractions(): void {
  emit('fetchExtractions', {
    text: searchQuery.value || '',
    page: currentPage.value,
    pageSize: pageSize.value,
    sortKey: 'created_at',
    sortDirection: 'desc',
  });
}

function handlePageChange(page: number): void {
  currentPage.value = page;
  fetchExtractions();
}

function handlePageSizeChange(size: number): void {
  pageSize.value = size;
  currentPage.value = 1;
  fetchExtractions();
}

/**
 * The field debounces its own keystrokes and fires this immediately on clear,
 * which is what the hand-rolled handler here used to arrange. The guard stays:
 * with nothing extracted and an empty term there is nothing to search for.
 */
function applySearch(value: string): void {
  if (props.originalTotalExtractions === 0 && value === '') return;
  currentPage.value = 1;
  fetchExtractions();
}

function clearSearch(): void {
  searchQuery.value = '';
  applySearch('');
}

// ==================== Row states ====================
/** Nothing extracted at all — as opposed to a filter that happens to be empty. */
const isEmpty = computed(
  () => props.extractionsLoaded && props.originalTotalExtractions === 0 && !props.loading
);

/** Rows exist, but this search matches none of them. */
const isNoResults = computed(
  () => !props.loading && props.extractions.length === 0 && props.originalTotalExtractions > 0
);

// ==================== Extraction Display Helpers ====================
/**
 * Get the display value for an extraction using formatDisplayValue for proper type handling
 */
function getExtractionDisplayValue(extraction: ContractManagementExtractionInDb): string {
  // Prioritize human overwrite
  const value = extraction.human_overwrite?.value ?? extraction.value;

  if (value === null) {
    return '—';
  }

  // Use formatDisplayValue to handle all supported types (currency, entities, dates, arrays, etc.)
  // Pass the response_type from the extraction to ensure proper formatting
  const responseType = extraction.response_type;

  return formatDisplayValue(value, responseType);
}

/** The page a row jumps to — a human override wins over the AI's own page. */
function extractionPage(extraction: ContractManagementExtractionInDb): number | null {
  return extraction.human_overwrite?.page === undefined
    ? extraction.page
    : extraction.human_overwrite.page;
}

function hasPageOverwrite(extraction: ContractManagementExtractionInDb): boolean {
  return isValueOverwritten(extraction.human_overwrite?.page, extraction.page);
}

/**
 * Handle page click - emit event to parent with extraction data
 */
function handlePageClick(extraction: ContractManagementExtractionInDb): void {
  const page = extractionPage(extraction);
  if (page !== null) {
    emit('pageClick', page, extraction);
  }
}

/**
 * Check if extraction has any actual overwrites (values that differ from original)
 * Only returns true if human_overwrite exists AND at least one field is actually different
 */
function hasAnyOverwrite(extraction: ContractManagementExtractionInDb): boolean {
  if (!extraction.human_overwrite) {
    return false;
  }

  const overwrite = extraction.human_overwrite;

  // Check if any field is actually different from the original
  return (
    isValueOverwritten(overwrite.value, extraction.value) ||
    isValueOverwritten(overwrite.reason, extraction.reason) ||
    isValueOverwritten(overwrite.page, extraction.page) ||
    isValueOverwritten(overwrite.highlighted_text, extraction.highlighted_text)
  );
}

/** The value itself was overridden — the badge and the accent weight say so. */
function isValueOverwrittenBy(extraction: ContractManagementExtractionInDb): boolean {
  return (
    hasAnyOverwrite(extraction) &&
    isValueOverwritten(extraction.human_overwrite?.value, extraction.value)
  );
}

function overwriteAuthor(extraction: ContractManagementExtractionInDb): string | undefined {
  const user = extraction.human_overwrite?.user;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must normalise to undefined
  return user?.full_name || user?.email || undefined;
}

// Expose refresh function for parent
defineExpose({
  refresh: fetchExtractions,
});

const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
const accentColor = gaiaAccent.base;
const alertBorder = gaiaStatus.alertBorder;
const alertSurface = gaiaStatus.alertSurface;
const alertColor = gaiaStatus.alert;
const successColor = gaiaStatus.successText;
const blankColor = gaiaNeutral.textBlank;
const monoFont = gaiaFonts.mono;
</script>

<template>
  <!-- The Extractions block from the Contract File Detail mock: a caps label
       with the count and a search on the right, then one GaiaDataTable whose
       three tracks are Field / Value / page·status. -->
  <n-flex vertical :size="12" class="extractions-section">
    <n-flex align="center" :size="12" :wrap="false">
      <gaia-text variant="label" size="micro" tone="faint">Extractions</gaia-text>
      <gaia-text v-if="props.totalExtractions > 0" variant="label" size="micro" tone="muted">
        {{ props.totalExtractions }}
      </gaia-text>
      <gaia-search-input
        v-model:value="searchQuery"
        :variant="props.isSplitView ? 'panel' : 'toolbar'"
        size="small"
        placeholder="Search fields and values"
        :disabled="props.extractionsLoaded && props.originalTotalExtractions === 0"
        :style="{ marginLeft: 'auto' }"
        :debounce="500"
        @search="applySearch"
      />
    </n-flex>

    <gaia-data-table :columns="columns" :action-width="0">
      <template #default="{ rowStyle }">
        <template v-if="props.loading">
          <gaia-table-skeleton
            :row-style="rowStyle"
            :cells="skeletonCells"
            :rows="6"
            :action="false"
          />
        </template>

        <gaia-empty-state v-else-if="isEmpty" title="Nothing has been extracted from this file yet">
          <template #icon>
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3.5 3.5h11v11h-11z" />
              <path d="M6 7h6M6 10h4" />
            </svg>
          </template>
          Extraction runs when a playbook or field set is applied to the document. Check back if a
          run is already queued.
          <template #actions>
            <gaia-button size="small" @click="emit('openDocument')">Open the document</gaia-button>
          </template>
        </gaia-empty-state>

        <gaia-empty-state v-else-if="isNoResults" :title="`No extraction matches “${searchQuery}”`">
          <template #icon>
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
            >
              <circle cx="8" cy="8" r="5" />
              <path d="M11.8 11.8 15.5 15.5" />
            </svg>
          </template>
          Search covers the field question and the extracted value. All
          {{ props.originalTotalExtractions }} extractions are still here — only this filter is
          empty.
          <template #actions>
            <gaia-button size="small" @click="clearSearch">Clear search</gaia-button>
          </template>
        </gaia-empty-state>

        <div
          v-for="extraction in props.extractions"
          v-else
          :key="extraction.uid"
          class="row"
          :style="rowStyle"
        >
          <!-- Field: the run's state leads, then the question, clamped to three
               lines so a long prompt can't push the value cell off screen. -->
          <div class="cell field-cell">
            <gaia-status-indicator
              :status="taskStatusMap[extraction.processing_state].status"
              :glyph="taskStatusMap[extraction.processing_state].glyph"
              :title="taskStatusMap[extraction.processing_state].label"
            />
            <gaia-text size="control" tone="ink" class="clamp-3" :title="extraction.prompt">
              {{ extraction.prompt }}
            </gaia-text>
          </div>

          <!-- Value, plus whatever qualifies it: the overwrite badge, the run's
               error, the approval. -->
          <n-flex vertical :size="5" class="cell value-cell">
            <n-flex align="center" :size="7" :wrap="false">
              <gaia-text
                size="control"
                class="clamp-2"
                :tone="isValueOverwrittenBy(extraction) ? 'accent' : 'ink'"
                :weight="isValueOverwrittenBy(extraction) ? 'semibold' : 'regular'"
                :title="getExtractionDisplayValue(extraction)"
              >
                {{ getExtractionDisplayValue(extraction) }}
              </gaia-text>
              <gaia-overwrite-badge
                v-if="isValueOverwrittenBy(extraction)"
                :by="overwriteAuthor(extraction)"
              />
            </n-flex>

            <div
              v-if="extraction.processing_state === 'failed' && extraction.error_text"
              class="row-error"
            >
              <span class="error-dot" />
              <gaia-text size="meta" tone="text" :style="{ lineHeight: 1.45 }">
                {{ extraction.error_text }}
              </gaia-text>
            </div>

            <n-flex v-if="extraction.approved" align="center" :size="5" :wrap="false">
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3.4"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="approved-check"
              >
                <path d="M4 12.5 9.5 18 20 6.5" />
              </svg>
              <gaia-text size="micro" class="approved-text">Approved</gaia-text>
            </n-flex>
          </n-flex>

          <!-- Edit only exists under the pointer: it is the row's one action and
               shouldn't sit in every row competing with the page chip. -->
          <n-flex align="center" justify="end" :size="6" :wrap="false" class="cell">
            <gaia-button
              size="tiny"
              class="edit-action"
              @click.stop="emit('editExtraction', extraction)"
            >
              <template #icon>
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M3 13h3l7-7-3-3-7 7z" />
                </svg>
              </template>
              Edit
            </gaia-button>

            <gaia-button
              v-if="extractionPage(extraction) !== null"
              size="tiny"
              class="page-chip"
              :class="{ overwritten: hasPageOverwrite(extraction) }"
              :title="`Open the document at page ${extractionPage(extraction)} and highlight the source text`"
              @click.stop="handlePageClick(extraction)"
            >
              <template #icon>
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M4 2.5h5l3 3v8H4z" />
                  <path d="M9 2.5v3h3" />
                </svg>
              </template>
              p.{{ extractionPage(extraction) }}
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                :style="{ marginLeft: '4px' }"
              >
                <path d="M4.5 2.5 8 6l-3.5 3.5" />
              </svg>
            </gaia-button>
            <gaia-text v-else size="control" class="no-page">no page</gaia-text>
          </n-flex>
        </div>
      </template>

      <template v-if="props.totalExtractions > 0 && !props.loading" #footer>
        <gaia-pagination
          :page="currentPage"
          :page-size="pageSize"
          :total="props.totalExtractions"
          @update:page="handlePageChange"
          @update:page-size="handlePageSizeChange"
        />
      </template>
    </gaia-data-table>
  </n-flex>
</template>

<style scoped>
/* In the split view the block keeps its own height so the PDF pane beside it
   governs the page scroll. */
.extractions-section {
  min-width: 0;
}

.row {
  display: grid;
  align-items: start;
  gap: 16px;
  padding: 12px 18px;
  border-bottom: 1px solid v-bind(rowBorder);
}

.row:hover {
  background: v-bind(rowHover);
}

.cell {
  min-width: 0;
}

.field-cell {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.field-cell :deep(.gaia-status-indicator) {
  flex: none;
  margin-top: 5px;
}

/* A prompt is a whole question, so it wraps — but three lines is the most a row
   may spend before the value beside it stops being readable. */
.clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-width: 0;
  line-height: 1.5;
}

.clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-width: 0;
  line-height: 1.5;
}

/* A failed run explains itself under its own value rather than in a tooltip. */
.row-error {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 7px 9px;
  border: 1px solid v-bind(alertBorder);
  border-radius: 8px;
  background: v-bind(alertSurface);
}

.error-dot {
  flex: none;
  width: 6px;
  height: 6px;
  margin-top: 4px;
  border-radius: 50%;
  background: v-bind(alertColor);
}

.approved-check,
.approved-text {
  flex: none;
  color: v-bind(successColor);
}

/* Edit is the row's one action and shouldn't sit in every row competing with
   the page chip — but it stays in the DOM so it is reachable by keyboard, and
   focus reveals it the way hover does. */
.edit-action {
  opacity: 0;
}

.row:hover .edit-action,
.edit-action:focus-visible {
  opacity: 1;
}

/* An overwritten page is a human's answer, so the chip carries the accent the
   overwrite badge uses rather than the neutral button edge. */
.page-chip.overwritten {
  border-color: v-bind(accentColor);
  color: v-bind(accentColor);
}

.no-page {
  color: v-bind(blankColor);
  font-family: v-bind(monoFont);
}
</style>
